import { useState, useEffect, useCallback } from "react";
import Facility from "../Facility/Facility";
import {
  updateBusinessDetail,
  getBusinessDetail,
  addYearTab,
  getYearTabList,
  deleteBusiness,
} from "../../../apis/Business/Business";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CompanyComponent from "../components/CompanyComponent";
import componentIcon from "../../../assets/design-revamp-company/company-component/company-component-icon-facility.svg";
import { AlertV2 } from "../../../components/Alert";

const EditCompany = ({ businessId }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [type, setType] = useState([]);
  const [ownership, setOwnership] = useState("");
  const [ownershipPerc, setOwnershipPerc] = useState("");
  const [percentage, setPercentage] = useState("");
  const [fullTimeEmployees, setFullTimeEmployees] = useState("");
  const [baseline, setBaseline] = useState("false");
  const [revenue, setRevenue] = useState("");
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [country, setCountry] = useState("");
  const [errorMessage, setErrorMessage] = useState({});
  const urlParams = new URLSearchParams(window.location.search);
  const year = urlParams.get("year");
  const [cutOffAddress, setCutOffAddress] = useState("");
  const [yearTabList, setYearTabList] = useState([]);
  const [deleteFacilityAlert, setDeleteFacilityAlert] = useState(false);
  const [checkedType, setCheckedType] = useState([
    {
      name_en: "Storage",
      name: t("Storage"),
      checked: false,
      space_percentage: null,
    },
    {
      name_en: "Production",
      name: t("Production"),
      checked: false,
      space_percentage: null,
    },
    {
      name_en: "Office",
      name: t("Office"),
      checked: false,
      space_percentage: null,
    },
    {
      name_en: "Housing",
      name: t("Housing"),
      checked: false,
      space_percentage: null,
    },
    {
      name_en: "Shop",
      name: t("Shop"),
      checked: false,
      space_percentage: null,
    },
  ]);

  useEffect(() => {
    setCheckedType((prevState) =>
      prevState.map((item) => ({
        ...item,
        name: t(item.name_en),
      }))
    );
  }, [t]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const navigate = useNavigate();
  const handleMove = useCallback(
    () => navigate("/company", { replace: true }),
    [navigate]
  );

  useEffect(() => {
    getYearTabList({ setYearTabList: setYearTabList });
    getBusinessDetail({
      id: businessId,
      setName: setName,
      setAddressLine1: setAddressLine1,
      setYearStart: setYearStart,
      setYearEnd: setYearEnd,
      setType: setType,
      setBaseline: setBaseline,
      setAddressLine2: setAddressLine2,
      checkedType: checkedType,
      setCheckedType: setCheckedType,
      setOwnership: setOwnership,
      setOwnershipPerc: setOwnershipPerc,
      setFullTimeEmployees: setFullTimeEmployees,
      setRevenue: setRevenue,
      setIsDataLoaded: setIsDataLoaded,
    });
  }, [businessId]);

  const handleFormDataChange = (updatedFormData) => {
    setName(updatedFormData.name);
    setAddressLine1(updatedFormData.addressLine1);
    setAddressLine2(updatedFormData.addressLine2);
    setType(updatedFormData.type);
    setOwnership(updatedFormData.ownership);
    setOwnershipPerc(updatedFormData.ownershipPerc);
    setFullTimeEmployees(updatedFormData.fullTimeEmployees);
    setBaseline(updatedFormData.baseline);
    setRevenue(updatedFormData.revenue);
    setYearStart(updatedFormData.yearStart);
    setYearEnd(updatedFormData.yearEnd);
    setCountry(updatedFormData.country);
  };

  const handleDelete = () => {
    setDeleteFacilityAlert(true);
  };

  const moveCompany = (selectedYear) => {
    // Parse yearStart and yearEnd strings into Date objects
    const startDate = new Date(yearStart);
    const endDate = new Date(yearEnd);

    // Check if parsing was successful
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error("Failed to parse yearStart or yearEnd into Date objects.");
      return;
    }

    // Move the company to the selected year while keeping the month and day the same
    const newYearStart = new Date(
      selectedYear,
      startDate.getMonth(),
      startDate.getDate()
    );
    const newYearEnd = new Date(
      selectedYear,
      endDate.getMonth(),
      endDate.getDate()
    );

    // Update the yearStart and yearEnd accordingly
    setYearStart(newYearStart);
    setYearEnd(newYearEnd);
  };

  let selectedYear = null;

  const handleSave = () => {
    const newErrorMessage = { ...errorMessage };
    // Validation logic
    if (!addressLine1.trim()) {
      newErrorMessage.address = ["Address is required."];
    } else {
      delete newErrorMessage.address; // Remove the error if the field is valid
    }

    if (typeof fullTimeEmployees === "string" && !fullTimeEmployees.trim()) {
      newErrorMessage.fullTimeEmployees = ["Full time employees is required."];
    } else {
      delete newErrorMessage.fullTimeEmployees; // Remove the error if the field is valid
    }

    if (yearStart && yearEnd) {
      const startDateParts = yearStart.split("-");
      const endDateParts = yearEnd.split("-");
      const startDate = new Date(
        startDateParts[2],
        startDateParts[1] - 1,
        startDateParts[0]
      );
      const endDate = new Date(
        endDateParts[2],
        endDateParts[1] - 1,
        endDateParts[0]
      );
      const oneYearInMillis = 365 * 24 * 60 * 60 * 1000;
      const differenceInMillis = endDate.getTime() - startDate.getTime();

      if (differenceInMillis > oneYearInMillis) {
        newErrorMessage.yearEnd = [
          "Year end must be at most 1 year after year start.",
        ];
      } else {
        delete newErrorMessage.yearEnd; // Remove the error if the field is valid
      }
    }

    setErrorMessage(newErrorMessage); // Set all errors at once

    // Stop execution if there are any errors
    if (Object.keys(newErrorMessage).length > 0) {
      return;
    }

    if (yearStart) {
      const parts = yearStart.split("-");
      if (parts.length === 3) {
        const [day, month, year] = parts.map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          selectedYear = year;
        } else {
          console.error("Invalid format for yearStart.");
          return;
        }
      } else {
        console.error("Invalid format for yearStart.");
        return;
      }
    }

    if (selectedYear && !yearTabList.includes(selectedYear.toString())) {
      addYearTab({
        yearData: selectedYear.toString(),
        callback: () => {
          // Add the newly created year tab to the list
          setYearTabList([...yearTabList, selectedYear.toString()]);
          // Move the company to the newly created year tab
          moveCompany(selectedYear.toString());
        },
      });
      save();
      navigate(`/company/add?year=${selectedYear}`);
      return;
    }

    save();
  };

  const save = () => {
    // Create a comma-separated string of types
    const typesString = checkedType
      .filter((t) => t.checked)
      .map((t) => t.name_en)
      .join(",");

    const space_percentage = checkedType
      .filter((t) => t.checked)
      .map((t) => ({
        type: t.name_en,
        percentage: parseInt(t.space_percentage),
      }));

    updateBusinessDetail({
      businessId: businessId,
      name,
      address1: addressLine1,
      address2: addressLine2,
      country,
      baseline: baseline === "Yes" || baseline === "Ja" ? true : false,
      yearStart,
      yearEnd,
      type: typesString, // Use the formatted comma-separated string
      space_type: space_percentage,
      ownership,
      ownershipPerc: percentage,
      setErrorMessage,
      fullTimeEmployees,
      selectedYear: selectedYear,
      revenue,
    });
  };

  return (
    <>
      <div
        className="companydetail"
        style={{ display: "flex", flexDirection: "row", paddingRight: "42px" }}
        id="facility-id"
      >
        <CompanyComponent
          name={name}
          address_line_1={cutOffAddress !== "" ? cutOffAddress : addressLine1}
          iconComponent={componentIcon}
        />

        {isDataLoaded && (
          <Facility
            initialData={{
              name,
              addressLine1,
              addressLine2,
              type,
              ownership,
              ownershipPerc,
              fullTimeEmployees,
              baseline,
              revenue,
              yearStart,
              yearEnd,
              country,
              checkedType,
            }}
            isNewFacility={false}
            onFormDataChange={handleFormDataChange}
            handleSave={handleSave}
            types={checkedType}
            setTypes={setCheckedType}
            errorMessage={errorMessage}
            setCurrentName={setName}
            setCutOffAddress={setCutOffAddress}
            setPercentage={setPercentage}
            handleDelete={handleDelete}
          />
        )}
      </div>
      <AlertV2
        open={deleteFacilityAlert}
        title={t("DELETE_FACILITY_2")}
        accept={() => {
          deleteBusiness({ businessId: businessId });
          handleMove();
        }}
        decline={() => setDeleteFacilityAlert(false)}
      />
    </>
  );
};

export default EditCompany;
